import React, {useCallback, useEffect, useState} from 'react'
import { createNewLevel, deleteLevel, getLevels, updateLevel } from '../../../../api/settings'
import styles from './LevelsManagementComponent.module.scss'
import BorderColorTwoToneIcon from '@mui/icons-material/BorderColorTwoTone'
import DeleteForeverTwoToneIcon from '@mui/icons-material/DeleteForeverTwoTone'
import MuiAlert, { AlertColor } from '@mui/material/Alert'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {
  Dialog,
  Snackbar,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  TextField,
  FormControl, InputLabel, Select, Grid, MenuItem, Avatar
} from '@mui/material'
import {createPreSignedUrl, deleteImage, getImagesList} from '../../../../api/files'
import ImageTwoToneIcon from '@mui/icons-material/ImageTwoTone';

type ImageType = {
  id: string,
  url: string,
  isPublic: boolean,
  price: number | null,
  isPaid: boolean,
  name: string,
  type: string
}

type LevelType = {
  id?: string
  image?: ImageType | null
  imageDisabled?: ImageType | null
  name: string
  xpGoal: number
}

export const LevelsManagementComponent = () => {
  const [levelList, setLevelList] = useState<LevelType[] | []>([])
  const [availableImages, setAvailableImages] = useState([])
  const [openSnackbar, setOpenSnackbar] = useState<{isOpen: boolean, alertType: AlertColor, message: string}>({
    isOpen: false,
    alertType: 'success',
    message: ''
  })
  const [isOpenDialog, setIsOpenDialog] = useState(false)
  const [newLevel, setNewLevel] = useState<any>({
    name: '',
    xpGoal: 0
  })
  const [isAscSort, setIsAscSort] = useState(true)
  const [isValidNewLevelXp, setIsValidNewLevelXp] = useState(true)
  const [isSelectedLevel, setIsSelectedLevel] = useState(false)
  const [uploadImagePreview, setUploadImagePreview] = useState<string>('')
  const [uploadDisabledImagePreview, setUploadDisabledImagePreview] = useState<string>('')
  const [isOpenActiveFileSelect, setIsOpenActiveFileSelect] = useState(false)
  const [isOpenDisabledFileSelect, setIsOpenDisabledFileSelect] = useState(false)

  useEffect(() => {
    (async () => {
      const levels = await getLevels()
      sortLevels(true, levels)
      setAvailableImages(await getImagesList({ type: 'LEVEL_ICON', take: 50 }))
    })()
  }, [])
  
  const sortLevels = useCallback((asc: boolean, levels: any[]) => {
    function byField(field: any) {
      return (a: any, b: any) => asc ? (a[field] > b[field] ? 1 : -1) : (a[field] < b[field] ? 1 : -1)
    }
  
    setLevelList(levels.sort(byField('xpGoal')))
  }, [isAscSort, levelList])
  
  const closeDialog = async () => {
    setIsOpenDialog(false)
    setNewLevel({
      name: '',
      xpGoal: 0,
      imageId: ''
    })
    setLevelList(await getLevels())
    setUploadImagePreview('')
    setUploadDisabledImagePreview('')
    setIsSelectedLevel(false)
  }
  
  const handleDeleteLevel = async (levelId: string) => {
    try {
      await deleteLevel(levelId);
      const updatedLevelList = levelList.filter((item) => item.id !== levelId);
      setLevelList(updatedLevelList);
      setOpenSnackbar({
        isOpen: true,
        message: `Level has been successfully deleted`,
        alertType: 'success'
      })
    } catch (e) {
      setOpenSnackbar({
        isOpen: true,
        message: 'Something went wrong, the level was not deleted, please try again',
        alertType: 'error'
      })
    }
  };
  
  const handleCreateNewLevel = async () => {
    try {
      if (isSelectedLevel) {
        await updateLevel(newLevel)
      } else {
        await createNewLevel(newLevel)
      }
      
      setOpenSnackbar({
        isOpen: true,
        message: `Level has been successfully ${newLevel.name.length ? 'updated' : 'created'}`,
        alertType: 'success'
      })
      closeDialog()
    } catch (e) {
      setOpenSnackbar({
        isOpen: true,
        message: `Something went wrong, the level was not ${newLevel.name.length ? 'updated' : 'created'}, please try again`,
        alertType: 'error'
      })
    }
  }
  
  const handleUploadImage = async (files: any) => {
    try {
      const preSignedImage = await createPreSignedUrl(
        { type: "LEVEL_ICON", name: files[0].name, isPublic: true}
      )
  
      if (preSignedImage.id) setNewLevel({...newLevel, imageId: preSignedImage.id});
  
      await fetch(preSignedImage.presignedUrl, {
        method: "PUT",
        body: files[0],
      });
      setUploadImagePreview(preSignedImage.url)
      setOpenSnackbar({
        isOpen: true,
        message: `Active image has been successfully uploaded`,
        alertType: 'success'
      })
    } catch (e) {
      setOpenSnackbar({
        isOpen: true,
        message: `Something went wrong, the Active image was not uploaded, please try again or other image`,
        alertType: 'error'
      })
    }
  }
  
  const handleUploadDisabledImage = async (files: any) => {
    try {
      const preSignedImage = await createPreSignedUrl(
        { type: "LEVEL_ICON", name: files[0].name, isPublic: true}
      )
  
      if (preSignedImage.id) setNewLevel({...newLevel, imageDisabledId: preSignedImage.id});
  
      await fetch(preSignedImage.presignedUrl, {
        method: "PUT",
        body: files[0],
      });
      setUploadDisabledImagePreview(preSignedImage.url)
      setOpenSnackbar({
        isOpen: true,
        message: `Disabled image has been successfully uploaded`,
        alertType: 'success'
      })
    } catch (e) {
      setOpenSnackbar({
        isOpen: true,
        message: `Something went wrong, the disabled image was not uploaded, please try again or other image`,
        alertType: 'error'
      })
    }
  }
  
  const handleRemoveImage = async (isActiveImage: boolean) => {
    try {
      const imageId = isActiveImage ? newLevel.imageId || newLevel.image.id :
        newLevel.imageDisabledId || newLevel.imageDisabled.id
      await deleteImage(imageId)
      setNewLevel({
        ...newLevel,
        ...(isActiveImage && { image: null }),
        ...(!isActiveImage && { imageDisabled: null }),
      })
      isActiveImage ? setUploadImagePreview('') : setUploadDisabledImagePreview('')
    } catch (e) {
      console.log(e)
    }
  }
  
  const changeNewLevelXp = (xpGoal: number) => {
    setNewLevel({ ...newLevel, xpGoal})
    const isExistXpValue = levelList?.find((item) => item.xpGoal === xpGoal && item.id !== newLevel.id)
    setIsValidNewLevelXp(!isExistXpValue)
  }
  
  const handleSelectActiveImage = (selectImage: ImageType) => {
    setIsOpenActiveFileSelect(false)
    setNewLevel({...newLevel, imageId: selectImage.id})
    setUploadImagePreview(selectImage.url)
  }
  
  const handleSelectDisabledImage = (selectImage: ImageType) => {
    setIsOpenDisabledFileSelect(false)
    setNewLevel({...newLevel, imageDisabledId: selectImage.id})
    setUploadDisabledImagePreview(selectImage.url)
  }

  return (
    <div>
      <div
        className="mt-9 flex items-center justify-end mr-5"
      >
        {isAscSort ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
        <span
          style={{ color: '#4a4a4a' }}
          className="cursor-pointer"
          onClick={() => {
            setIsAscSort(!isAscSort)
            sortLevels(!isAscSort, levelList)
          }}
        >
          Sort by XP
        </span>
      </div>
      <div className={styles.levelsContainer}>
        {levelList?.map((item) => {
          return (
            <div className={styles.levelsItem}>
              <div className={styles.title}>
                <span>{item.name}</span>
                <span>XP - {item.xpGoal}</span>
              </div>
              {(item.image?.url || item?.imageDisabled?.url) && (
                <img
                  src={item.image?.url || item?.imageDisabled?.url}
                  alt="levelIcon"
                  className="rounded-md"
                />
              )}
              <div className={styles.actionContainer}>
                <BorderColorTwoToneIcon color="warning" onClick={() => {
                  setNewLevel(item)
                  setUploadImagePreview(item?.image?.url || '')
                  setUploadDisabledImagePreview(item?.imageDisabled?.url || '')
                  setIsSelectedLevel(true)
                  setIsOpenDialog(true)
                }} />
                <DeleteForeverTwoToneIcon
                  color="error"
                  className="ml-5"
                  onClick={() => {
                    if(item?.id) handleDeleteLevel(item.id)
                  }}
                />
              </div>
            </div>
          )
        })}
      </div>
      <Button variant="contained" onClick={() => setIsOpenDialog(true)} className="!mt-10">
        Create new level
      </Button>
      <Snackbar
        open={openSnackbar.isOpen}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar({...openSnackbar, isOpen: false})}
      >
        <MuiAlert
          onClose={() => setOpenSnackbar({...openSnackbar, isOpen: false})}
          severity={openSnackbar.alertType}
          sx={{ width: '100%' }}
        >
          {openSnackbar.message}
        </MuiAlert>
      </Snackbar>
      <Dialog open={isOpenDialog} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{`${isSelectedLevel ? 'Update' : 'Create new'} level`}</DialogTitle>
        <DialogContent>
          <DialogContentText className="!mb-5">
            {`Fill in the form and press the "${isSelectedLevel ? 'Update' : 'Create new'} level" button`}
          </DialogContentText>
        </DialogContent>
        <FormControl fullWidth className="!px-5 !pb-5">
          <TextField
            value={newLevel.name}
            label="Level name"
            variant="outlined"
            onChange={(event) =>
              setNewLevel({ ...newLevel, name: event.target.value})
            }
            required
          />
          <TextField
            error={!isValidNewLevelXp}
            value={newLevel.xpGoal.toString()}
            label="Xp goal"
            variant="outlined"
            onChange={(event) =>
              changeNewLevelXp(Number(event.target.value))
            }
            type="number"
            required
            className="!mt-5 p:text-red"
            helperText={!isValidNewLevelXp ? "XP goal value cannot be repeated with the values of other levels" : " "}
          />
          <div>
            {(uploadImagePreview || uploadDisabledImagePreview) &&
              <div className="flex items-start justify-between">
                {uploadImagePreview && (
                  <div className="flex-col w-max">
                    <label
                      className="flex items-center justify-center my-2 text-sm text-gray"
                    >
                      Preview active image
                    </label>
                    <img src={uploadImagePreview} alt="image-preview" className="w-[40%] h-auto my-0 mx-auto" />
                  </div>
                )}
                {uploadDisabledImagePreview && (
                  <div className="flex-col w-max">
                    <label
                      className="flex items-center my-2 justify-center text-sm text-gray"
                    >
                      Preview disabled image
                    </label>
                    <img src={uploadDisabledImagePreview} alt="disabled-image-preview" className="w-[40%] h-auto my-0 mx-auto" />
                  </div>
                )}
              </div>
            }
            {(uploadImagePreview || uploadDisabledImagePreview) && (
              <div className="mt-5 flex justify-between">
                {uploadImagePreview && (
                  <Button variant="outlined" color="error" onClick={() => handleRemoveImage(true)}>
                    Remove active image
                  </Button>
                )}
                {uploadDisabledImagePreview && (
                  <Button variant="outlined" color="error" onClick={() => handleRemoveImage(false)}>
                    Remove disabled image
                  </Button>
                )}
              </div>
            )}
            <input
              accept="image/*"
              style={{ display: "none" }}
              id="active-button-file"
              multiple
              type="file"
              onChange={(event) =>
                handleUploadImage(event.target.files)
              }
            />
            <input
              accept="image/*"
              style={{ display: "none" }}
              id="disabled-button-file"
              multiple
              type="file"
              onChange={(event) =>
                handleUploadDisabledImage(event.target.files)
              }
            />
            <div className="flex items-center justify-between">
              <div className="w-full">
                <FormControl fullWidth>
                  <InputLabel id="active-button-file">Select active image</InputLabel>
                  <Select
                    labelId="active-button-file"
                    id="select-active-file-component"
                    value={null}
                    label="Select active image"
                    open={isOpenActiveFileSelect}
                    onOpen={() => setIsOpenActiveFileSelect(true)}
                    onClose={() => setIsOpenActiveFileSelect(false)}
                  >
                    <Grid container spacing={0} className="max-w-[550px] max-h-[150px] overflow-auto">
                      {availableImages.map((item: any) => (
                        <Grid item md={0}>
                          <MenuItem value={item.id} onClick={() => handleSelectActiveImage(item)}>
                            <Avatar variant="rounded" src={item.url}>
                              <ImageTwoToneIcon />
                            </Avatar>
                            <span>{item.label}</span>
                          </MenuItem>
                        </Grid>
                      ))}
                    </Grid>
                  </Select>
                </FormControl>
                <p className="mt-2 mb-2 text-xs w-full text-center font-bold text-gray">OR</p>
                <label
                  htmlFor="active-button-file"
                  className="flex items-center justify-center p-3 rounded-md bg-metal text-white w-full text-center cursor-pointer"
                >
                  Upload active image
                </label>
              </div>
              <div className="w-full ml-5">
                <FormControl fullWidth>
                  <InputLabel id="disabled-button-file">Select disabled image</InputLabel>
                  <Select
                    labelId="disabled-button-file"
                    id="select-disabled-file-component"
                    value={null}
                    label="Select active image"
                    open={isOpenDisabledFileSelect}
                    onOpen={() => setIsOpenDisabledFileSelect(true)}
                    onClose={() => setIsOpenDisabledFileSelect(false)}
                  >
                    <Grid container spacing={0} className="max-w-[550px] max-h-[150px] overflow-auto">
                      {availableImages.map((item: any) => (
                        <Grid item md={0}>
                          <MenuItem value={item.id} onClick={() => handleSelectDisabledImage(item)}>
                            <Avatar variant="rounded" src={item.url}>
                              <ImageTwoToneIcon />
                            </Avatar>
                            <span>{item.label}</span>
                          </MenuItem>
                        </Grid>
                      ))}
                    </Grid>
                  </Select>
                </FormControl>
                <p className="mt-2 mb-2 text-xs w-full text-center font-bold text-gray">OR</p>
                <label
                  htmlFor="disabled-button-file"
                  className="flex items-center justify-center p-3 rounded-md bg-metal text-white w-full text-center cursor-pointer"
                >
                  Upload disabled image
                </label>
              </div>
            </div>
          </div>
          <div className="flex justify-between content-center mt-5">
            <Button variant="outlined" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateNewLevel}
              disabled={newLevel.name.length <= 2 || !isValidNewLevelXp}
            >
              {`${isSelectedLevel ? 'Update' : 'Create new'} level`}
            </Button>
          </div>
        </FormControl>
      </Dialog>
    </div>
  )
} 